import { JsonFormErrorFloatingPane } from '../JsonFormErrorFloatingPane';
import { fakeRequired, uppercaseOptions, lightStyle } from '../ThinkalphaTheme';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { rankWith, isEnumControl } from '@jsonforms/core';
import type { ControlProps, OwnPropsOfEnum } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { cn } from '@thinkalpha/ui-core/utils';
import type { KeysWithoutReturnObjects } from 'i18next';
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Errors, Description, Label } from 'src/components/JsonForm/helpers';
import { useJsonFormExternalConfig } from 'src/components/JsonForm/JsonFormExternalConfigContext';
import {
    PopoverTrigger,
    Button,
    PopoverContent,
    Command,
    CommandInput,
    CommandEmpty,
    CommandList,
    CommandGroup,
    CommandItem,
    Popover,
    SelectValue,
    SelectTrigger,
    SelectItem,
    SelectContent,
    Select,
} from 'src/components/ui';
import { useEvent } from 'src/hooks/useEvent';
import {
    QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL,
    QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE,
} from 'src/lib/orders/removePropsFromOrderData';

const ADVANCED_SELECT_THRESHOLD = 20;

export const EnumControlComponent = ({
    path,
    data,
    handleChange,
    description,
    errors,
    label,
    required,
    visible,
    schema,
}: ControlProps & OwnPropsOfEnum) => {
    const { t } = useTranslation();
    const config = useJsonFormExternalConfig();

    const hasErrors = errors.length > 0;

    const allowUnset = (schema as any)['x-allow-unset'] === true;

    const options = useMemo(() => {
        let baseOptions = schema.enum || [];

        if (schema.const) {
            if (data !== schema.const) {
                setTimeout(() => handleChange(path, schema.const));
            }
            baseOptions = [schema.const];
        }

        // Add "— None —" option to UI if x-allow-unset is true
        return allowUnset ? [QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE, ...baseOptions] : baseOptions;
    }, [data, handleChange, path, schema.const, schema.enum, allowUnset]);

    useEffect(() => {
        // Skip auto-selection logic if allowUnset is true
        if (allowUnset) {
            return;
        }

        if (options.length === 1 && data !== options[0] && options[0] !== QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE) {
            setTimeout(() => handleChange(path, options[0]));
        }

        // Only auto-select if not allowing unset and data is invalid
        if (options.length > 0 && !options.includes(data)) {
            handleChange(path, options[0]);
        }
    }, [options, data, handleChange, path, allowUnset]);

    if (fakeRequired.includes(path)) {
        required = true; // make it look like required
    }

    const isLight = lightStyle.includes(path);

    const uppercase = uppercaseOptions.includes(path);

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const handleOnSelect = useEvent((value: unknown) => {
        handleChange(path, value);
        setIsPopoverOpen(false);
    });

    // Map the actual data value to UI value for display
    const displayValue = useMemo(() => {
        if (data === QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE || data === undefined) {
            return QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE;
        }
        return data;
    }, [data]);

    const selectedOption = useMemo(() => {
        if (displayValue === QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE) {
            return QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL;
        }
        return data ? options.find((o) => o === data) : '';
    }, [data, displayValue, options]);

    return visible ? (
        <div>
            <div className="flex items-center gap-2">
                <Label label={label} required={required} />
                <Description description={description} />
            </div>

            <JsonFormErrorFloatingPane hasErrors={hasErrors} errors={<Errors errors={errors} />}>
                {options.length <= ADVANCED_SELECT_THRESHOLD && (
                    <Select onValueChange={handleOnSelect} value={displayValue}>
                        <SelectTrigger
                            variant={isLight ? 'light' : 'default'}
                            dimensions="sm"
                            className={cn(
                                uppercase && 'uppercase',
                                hasErrors &&
                                    (isLight
                                        ? 'bg-[#fbcdc1] dark:border-[#7a0808]'
                                        : 'bg-[#1D110E] dark:border-[#4C1919]'),
                            )}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {options.map((o) => {
                                const displayLabel =
                                    o === QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE
                                        ? QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL
                                        : o;
                                return (
                                    <SelectItem
                                        key={o}
                                        value={o}
                                        className={cn(
                                            uppercase && o !== QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE && 'uppercase',
                                        )}
                                    >
                                        {
                                            t(
                                                `${config.translationPrefix}::options::${displayLabel}` as KeysWithoutReturnObjects['json'],
                                                displayLabel,
                                            ) as unknown as string
                                        }
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                )}
                {options.length > ADVANCED_SELECT_THRESHOLD && (
                    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant={isLight ? 'light' : 'outline'}
                                size="2xs"
                                role="combobox"
                                className={cn(
                                    'w-full justify-between overflow-hidden',
                                    !data && 'text-gray-250',
                                    uppercase && 'uppercase',
                                    hasErrors &&
                                        (isLight
                                            ? 'bg-[#fbcdc1] dark:border-[#7a0808]'
                                            : 'bg-[#1D110E] dark:border-[#4C1919]'),
                                )}
                                disabled={false}
                            >
                                <span>
                                    {
                                        t(
                                            `${config.translationPrefix}::options::${selectedOption}` as KeysWithoutReturnObjects['json'],
                                            selectedOption,
                                        ) as unknown as string
                                    }
                                </span>
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className="ml-2 h-2.5 w-2.5 shrink-0 opacity-50"
                                />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0">
                            <Command>
                                <CommandInput placeholder="Search..." />
                                <CommandEmpty>No results found.</CommandEmpty>
                                <CommandList>
                                    <CommandGroup>
                                        {options.map((o) => {
                                            const displayLabel =
                                                o === QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE
                                                    ? QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL
                                                    : o;
                                            return (
                                                <CommandItem value={o} key={o} onSelect={() => handleOnSelect(o)}>
                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                        className={cn(
                                                            'mr-2',
                                                            o === displayValue ? 'opacity-100' : 'opacity-0',
                                                        )}
                                                    />

                                                    <span
                                                        className={cn(
                                                            uppercase &&
                                                                o !== QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE &&
                                                                'uppercase',
                                                        )}
                                                    >
                                                        {
                                                            t(
                                                                `${config.translationPrefix}::options::${displayLabel}` as KeysWithoutReturnObjects['json'],
                                                                displayLabel,
                                                            ) as unknown as string
                                                        }
                                                    </span>
                                                </CommandItem>
                                            );
                                        })}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                )}
            </JsonFormErrorFloatingPane>
        </div>
    ) : null;
};

export const EnumControlTester = rankWith(
    4, //increase rank as needed
    isEnumControl,
);

export const EnumControl = withJsonFormsControlProps(EnumControlComponent);
